import styled, { css } from "styled-components";

const ASIDE_W = 220;
const ASIDE_W_COLLAPSED = 68;

export const Aside = styled.aside<{ $collapsed: boolean }>`
  flex: 0 0 auto;
  position: sticky;
  top: 0;
  height: 100dvh;
  width: ${(p) => (p.$collapsed ? ASIDE_W_COLLAPSED : ASIDE_W)}px;

  background: linear-gradient(180deg, #f0f9ff 0%, #e6f2ff 100%);
  color: #0f172a;
  border-right: 1px solid #cfe3ff;

  padding: 8px;
  overflow: hidden auto;
  transition: width 0.2s ease;
  z-index: 40;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cfe3ff;
    border-radius: 6px;
  }
`;

export const TopBar = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.$collapsed ? "center" : "flex-start")};
  min-width: 0;
  padding: 8px;
  gap: 8px;
  cursor: pointer;
`;

export const LogoImg = styled.img<{ $collapsed?: boolean }>`
  height: ${(p) => (p.$collapsed ? "22px" : "30px")};
  width: auto;
  flex: 0 0 auto;
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
`;

export const BrandText = styled.span`
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5b7fa7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  min-width: 0;
  max-width: 100%;
  transition: opacity 0.2s ease, width 0.2s ease, margin 0.2s ease;
`;

export const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const SectionTitle = styled.div<{ $collapsed?: boolean }>`
  padding: 8px 4px;
  font-size: 12px;
  color: #5b7fa7;
  letter-spacing: 0.06em;
  transition: opacity 0.2s ease, height 0.2s ease;

  ${(p) =>
    p.$collapsed &&
    css`
      height: 0;
      padding-top: 0;
      padding-bottom: 0;
      opacity: 0;
      pointer-events: none;
    `}
`;

export const Item = styled.button<{
  $active?: boolean;
  $collapsed?: boolean;
  $danger?: boolean;
}>`
  --hover: rgba(59, 130, 246, 0.12);
  --active-bg: #dbeafe;
  --accent: #3b82f6;

  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;

  padding: 10px 10px;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  text-align: left;
  position: relative;
  border-radius: 8px;

  &:hover {
    background: var(--hover);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.45);
  }

  ${(p) =>
    p.$active &&
    css`
      background: var(--active-bg);
      font-weight: 600;

      &::before {
        content: "";
        position: absolute;
        left: 0;
        top: 8px;
        bottom: 8px;
        width: 4px;
        background: var(--accent);
        border-radius: 0 3px 3px 0;
      }
    `}

  ${(p) =>
    p.$collapsed &&
    css`
      justify-content: center;
    `}

  ${(p) =>
    p.$danger &&
    css`
      --hover: rgba(239, 68, 68, 0.12);
      --active-bg: #fee2e2;
      --accent: #ef4444;

      &:hover {
        color: #b91c1c;
      }
    `}
`;

export const Label = styled.span`
  flex: 1 1 auto;
  font-size: 14px;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: opacity 0.2s ease, width 0.2s ease, margin 0.2s ease;
`;

export const Bottom = styled.div`
  margin-top: auto;
  padding-top: 8px;
  padding-bottom: 6px;
`;

export const Divider = styled.hr`
  border: 0;
  height: 1px;
  background: #cfe3ff;
  margin: 8px 0 8px;
`;

export const Hint = styled.div<{ $collapsed?: boolean }>`
  font-size: 11px;
  color: #5b7fa7;
  padding: 4px 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: ${(p) => (p.$collapsed ? 0 : 1)};
  transition: opacity 0.2s ease;
`;

/* --- 프로필 --- */
export const ProfileBox = styled.div<{
  $collapsed?: boolean;
  $isAdmin?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.$collapsed ? "center" : "flex-start")};
  gap: ${(p) => (p.$collapsed ? "0" : "10px")};
  margin: 10px 8px 14px;
  border-radius: 12px;

  box-shadow: inset 0 1px 3px rgba(255, 255, 255, 0.7);
  transition: all 0.25s ease;

  &:hover {
    background: linear-gradient(135deg, #f0f8ff 0%, #ffffff 100%);
    transform: translateY(-1px);
  }
`;

export const ProfileIcon = styled.svg`
  width: 34px;
  height: 34px;
  padding: 6px;
  border-radius: 50%;
  background: linear-gradient(180deg, #dbeafe 0%, #bfdbfe 100%);
  color: #2563eb;
  flex-shrink: 0;
  transition: background 0.25s ease;

  ${ProfileBox}:hover & {
    background: linear-gradient(180deg, #cfe3ff 0%, #e0f2fe 100%);
  }
`;

export const Nickname = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1e3a8a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.02em;
`;

/** 역할별 컬러: A(보라), O(초록), D(파랑), U(회색) */
export const AdminBadge = styled.span<{ $role: "A" | "O" | "D" | "U" }>`
  margin-left: auto;
  padding: 2px 6px;
  min-width: 18px;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  border-radius: 6px;
  letter-spacing: 0.04em;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.08);
  text-transform: uppercase;

  ${(p) =>
    p.$role === "A" &&
    css`
      background: linear-gradient(90deg, #8b5cf6, #6d28d9);
    `}
  ${(p) =>
    p.$role === "O" &&
    css`
      background: linear-gradient(90deg, #10b981, #059669);
    `}
  ${(p) =>
    p.$role === "D" &&
    css`
      background: linear-gradient(90deg, #3b82f6, #2563eb);
    `}
  ${(p) =>
    p.$role === "U" &&
    css`
      background: linear-gradient(90deg, #9ca3af, #6b7280);
    `}
`;
